import {
    Button, Card, CircularProgress, Dialog, DialogContent, DialogTitle,
    Divider, FormGroup, FormHelperText, FormLabel, Grid, IconButton, Switch, TextField
} from "@mui/material";
import { IconX } from "@tabler/icons-react";
import { removeEmptyKeys } from "helper/encodeData";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { getRegionOption } from "store/app/farm-management/farm-records/actions";
import { ErrorAlert } from "ui-component/custom-components/alertMessage";
import { Required } from "ui-component/custom-components/mandatory";
import {
    stateEditErrorClear,
    stateEdit
} from "store/app/form-management/state-configuration/actions";
import {
    AllowedSpecialCharacters,
    AllowedSpecialCharactersPaste
} from "helper/regex";
import { getUsersOption } from "store/app/customer-management/actions";
import VWDropdown from "components/dropdown";
import { unionBy } from 'lodash';

const EditState = (props) => {

    const { open, filter, paginationData, errorMessage, onClose, isPending, stateDetails } = props;

    const [stateData, setStateData] = useState();
    const [isActive, setIsActive] = useState();
    const [errors, setErrors] = useState({});

    useEffect(() => {
        props.stateEditErrorClear();
        if (stateDetails) {
            setStateData({
                ...stateDetails,
                userIds: stateDetails?.userLst?.map((user) => user.userId)
            });
            setIsActive(stateDetails?.isActive);
            setSelectedRegion({ label: stateDetails?.regionName, value: stateDetails?.regionId });
            const assignedUsers = stateDetails?.userLst?.map((user) => {
                return {
                    label: `${user.userName} | ${user.userEmail}`,
                    value: user.userId,
                }
            })
            setSelectedUsers(assignedUsers)
        }
        setErrors({});
    }, [open])

    //--------------------Region---------------------------
    const [regionOptions, setRegionOptions] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState();

    const loadRegionOption = (filter, page, pageSize) => {
        return props.getRegionOption(filter, "create", page, pageSize)
    }

    //--------------------User-----------------------------
    const [usersOptions, setUsersOptions] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);

    const loadUsersOption = (filter, page, pageSize) => {
        return props.getUsersOption(filter, page, pageSize)
    }

    const validateOnchange = (e) => {
        const { name, value } = e.target;

        setStateData({
            ...stateData,
            [name]: value
        })
        setErrors({
            ...errors,
            [name]: null,
        })
    }

    const handleActiveStatusChange = () => {
        setIsActive(!isActive);
        setStateData({
            ...stateData,
            isActive: !isActive
        })
    };

    const handleRegionSelect = (option) => {
        const { label, value } = option || {};
        setSelectedRegion(option);
        setStateData({
            ...stateData,
            regionName: label,
            regionId: value
        })
        setErrors({
            ...errors,
            regionName: null,
        })
    }

    const handleAssignedUsersSelect = (option, name) => {
        const optionArray = option?.map(field => field.value);
        setStateData({
            ...stateData,
            [name]: optionArray,
        })
        setSelectedUsers(option);
    }

    const handleStateEdit = (e) => {
        e.preventDefault();

        const { regionName, stateName } = stateData || {};
        const currentErrors = {};

        if (!stateName && !stateName?.trim()) {
            currentErrors.stateName = 'State/Export name is required';
        }
        if (!regionName && !regionName?.trim()) {
            currentErrors.regionName = 'Region name is required';
        }
        setErrors(currentErrors);

        if (Object.keys(currentErrors).length === 0) {
            props.stateEdit(stateData, filter, paginationData);
        }
    }

    const validateClose = (e, reason) => {
        if (reason !== 'backdropClick') {
            onClose();
        }
    }

    return (
        <>
            <Dialog open={open} maxWidth={'xs'} fullWidth onClose={validateClose}>
                {open && (
                    <>
                        <Card sx={{ overflow: 'auto' }} >
                            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: "center", pt: 0, pb: 0 }}>
                                Edit State/Export
                                <IconButton
                                    aria-label="close"
                                    onClick={onClose}
                                >
                                    <IconX />
                                </IconButton>
                            </DialogTitle>
                            <Divider />
                            <DialogContent>
                                {errorMessage && <ErrorAlert content={errorMessage} />}
                                <form>
                                    <Grid container rowGap={3} justifyContent={'center'} direction={'column'}>
                                        <Grid item >
                                            <TextField
                                                label={<div >State/Export Name <Required /></div>}
                                                sx={{ mt: 1 }}
                                                fullWidth
                                                autoFocus
                                                autoComplete="off"
                                                size="small"
                                                name='stateName'
                                                type='text'
                                                onChange={validateOnchange}
                                                onKeyDown={AllowedSpecialCharacters}
                                                onPaste={AllowedSpecialCharactersPaste}
                                                value={stateData?.stateName}
                                                error={errors?.stateName}
                                                helperText={errors?.stateName}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <VWDropdown
                                                label="Region Name"
                                                name="regionName"
                                                multiple={false}
                                                // disablePortal={true}
                                                getOptions={loadRegionOption}
                                                onSelect={handleRegionSelect}
                                                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                                selectedOption={selectedRegion || null}
                                                options={regionOptions}
                                                setOptions={(options) => {
                                                    setRegionOptions((prev) => unionBy(prev, options, "value"));
                                                }}
                                                isRequired={true}
                                                error={errors?.regionName}
                                            />
                                        </Grid>
                                        <Grid item sx={{ width: "100%" }}>
                                            <VWDropdown
                                                label="Assigned Users"
                                                name="userIds"
                                                multiple={true}
                                                // disablePortal={true}
                                                getOptions={loadUsersOption}
                                                onSelect={handleAssignedUsersSelect}
                                                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                                selectedOption={selectedUsers || []}
                                                options={usersOptions}
                                                setOptions={(options) => {
                                                    setUsersOptions((prev) => unionBy(prev, options, "value"));
                                                }}
                                                selectedLimit="skip"
                                            />
                                            <FormHelperText>Note: States/Exports are automatically assigned to administrators.</FormHelperText>
                                        </Grid>
                                        <Grid item >
                                            <FormGroup>
                                                <FormLabel sx={{ fontWeight: 'bold', color: 'black' }}>State/Export Status</FormLabel>
                                                <Grid container alignItems={'center'}>
                                                    <Grid item sx={{ color: 'black' }}>Inactive</Grid>
                                                    <Grid item>
                                                        <Switch
                                                            color='success'
                                                            onChange={handleActiveStatusChange}
                                                            checked={stateData?.isActive}
                                                        />
                                                    </Grid>
                                                    <Grid item sx={{ color: 'black' }}>Active</Grid>
                                                </Grid>
                                            </FormGroup>
                                        </Grid>
                                        <Grid container justifyContent={'right'}>
                                            <Button
                                                variant="contained"
                                                onClick={handleStateEdit}
                                                size="small"
                                                color="primary"
                                                disabled={isPending || Object.keys(removeEmptyKeys(errors))?.length > 0}
                                            >
                                                {isPending ? <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <CircularProgress size={'20px'} color='inherit' />
                                                    Loading...
                                                </div>
                                                    : 'Submit'
                                                }
                                            </Button>
                                            <Button
                                                variant="contained"
                                                onClick={onClose}
                                                sx={{ ml: 2 }}
                                                color='error'
                                                size="small"
                                            >
                                                Close
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </form>
                            </DialogContent>
                        </Card>
                    </>
                )}
            </Dialog >
        </>
    )
}

const mapStateToProps = (state) => ({
    stateDetails: state.states.stateDetails,
    isPending: state.states.isStateEditPending,
    errorMessage: state.states.stateEditErrorMessage,
})

const mapDispatchToProps = (dispatch) => ({
    stateEdit: (formData, filter, paginationData) => dispatch(stateEdit(formData, filter, paginationData)),
    stateEditErrorClear: () => dispatch(stateEditErrorClear()),
    getRegionOption: (filter, type, page, pageSize) => dispatch(getRegionOption(filter, type, page, pageSize)),
    //--------------------User---------------------------
    getUsersOption: (filter, page, pageSize) => dispatch(getUsersOption(filter, page, pageSize)),

})

export default connect(mapStateToProps, mapDispatchToProps)(EditState);